import { Component, ErrorHandler, EventEmitter, inject, Input, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EMPTY, merge, Observable, Subject } from 'rxjs';
import { auditTime, catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { Building } from '@miq/domain/pv';
import { UpdateStackingPlanInfo } from './stacking-plan-container/floor-container/floor/floor.model';
import { FiltersService } from './filters-toolbar/filters.service';
import { IconLoaderService } from './icon-loader.service';
import { LocalizationOptions, ReplaceSpaceEvent } from './models';
import { EfficiencyTransport, ReplaceSpaceAction } from './models/efficiencies.models';
import { FloorClass } from './models/floor.class';
import { SpaceEventsService } from './space-events.service';
import { Toolbar } from './stacking-plan-container/actions-toolbar/actions-toolbar.model';
import { StackViewType, StackingPlanContainer } from './stacking-plan-container/stacking-plan.model';
import { StackingPlanService } from './stacking-plan.service';
import { LinkOpportunity } from '../lib/models/linked-opportunity.models';
import { SpLinkOpportunityService } from './shared/services/sp-link-opportunity.service';
import { EDPStackingPlan, SPDataStatus, StackingPlanReviewer } from './stacking-plan.model';
import { LocalizationOptionsService } from './localization-option.service';
import { StackPlanViewStateService } from './state/sp-view-state.service';
import { UsageTypes } from './shared/constants/usagetypes.const';
import { convertBuildingToTenantSummaryMap } from './property-view/floor-space-details/tenant-summary/tenant-summary.utils';
import { TenantSummaryService } from './property-view/floor-space-details/tenant-summary/tenant-summary.service';
import { Router } from '@angular/router';
import { getPropertyTypeLabel } from '@miq/utils';

@UntilDestroy()
@Component({
  selector: 'sp-stacking-plan',
  templateUrl: './stacking-plan.component.html',
  styleUrls: ['./stacking-plan.component.scss'],
  providers: [TenantSummaryService],
  standalone: false,
})
export class StackingPlanComponent implements OnInit, OnDestroy {
  stackView: StackViewType = StackViewType.Default;
  STACK_VIEWS = StackViewType;

  get recordId(): number {
    return this.spStateViewService.getState().id;
  }

  /** property EDP ID used to retrieve its stacking plan */
  @Input() set propertyId(id: number) {
    if (id != null) {
      this.spStateViewService.setPropertyId(id);
      this.stackingPlanService.setPropertyId(id);
    }
  }

  @Input() set localizationConfigs(opts: LocalizationOptions) {
    this.localizationOptionsService.setLocalizationConfigs(opts);
  }
  get localizationConfigs(): LocalizationOptions {
    return this.localizationOptionsService.getInstance();
  }

  @Input() createSpace: ReplaceSpaceAction<ReplaceSpaceEvent>;
  @Input() replaceSpace: ReplaceSpaceAction<ReplaceSpaceEvent>;

  @Output() updateEdpPropFloorData: EventEmitter<Toolbar> = new EventEmitter();
  @Output() selectedUsage: EventEmitter<string> = new EventEmitter();

  @Input() linkedOpportunityInput: LinkOpportunity<any>;

  /// add an input setter method for #propertyTypes & #selectedPropertyType
  @Input() set availablePropertyTypes(
    propertyTypes: {
      key: string | null;
      ref_property_usage_type_desc: string;
      hasAccess: boolean;
      tooltip: string | null;
    }[]
  ) {
    this.propertyTypes = (propertyTypes ?? []).map?.(usage => ({
      key: usage.ref_property_usage_type_desc,
      value: getPropertyTypeLabel(usage.ref_property_usage_type_desc),
      hasAccess: usage.hasAccess,
      tooltip: !usage.hasAccess ? 'recordsSearch.userPropertyPermission' : null,
    }));

    const hasAllAccess = propertyTypes.every(usage => usage.hasAccess);
    this.propertyTypes.unshift({
      key: null,
      value: 'All',
      hasAccess: hasAllAccess,
      tooltip: !hasAllAccess ? 'recordsSearch.userPropertyPermission' : null,
    });
    const activePropertyType = this.spStateViewService.getState().propertyType;

    if (propertyTypes.some(p => p.ref_property_usage_type_desc === activePropertyType) || activePropertyType === null) {
      return;
    }

    const [primaryUsage = null] = propertyTypes;
    if (primaryUsage !== null) {
      this.activePropertyType = primaryUsage.ref_property_usage_type_desc;
    }
  }

  @Input() set activePropertyType(value: string) {
    if (value != null) {
      this.selectedPropertyType.set(value);
      this.spStateViewService.setPropertyType(value);
      this.selectedUsage.emit(value);
      return;
    }

    const activePropertyType = this.spStateViewService.getState().propertyType;
    if (activePropertyType !== value) {
      this.selectedPropertyType.set(activePropertyType);
      this.selectedUsage.emit(activePropertyType);
    }
  }

  public stackingPlanDataLoaded = false;
  public get containerData(): StackingPlanContainer {
    return this.stackingPlanService.containerDataSnap;
  }

  public set containerData(value: StackingPlanContainer) {
    this.stackingPlanService.setContainerData(value);
    this.stackingPlanDataLoaded = value != null;
  }

  buildingData: Building = null;

  reviewerData: StackingPlanReviewer = null;
  detailedStackData: EDPStackingPlan = null;

  public propertyTypes: { key: string | null; value: string; hasAccess: boolean; tooltip: string | null }[] = [];
  public selectedPropertyType = signal<string | null>(null);

  readonly #loadRequests = new Subject<{ id: number; params: ReturnType<StackPlanViewStateService['getState']> }>();
  readonly #errorHandler = inject(ErrorHandler);

  constructor(
    private stackingPlanService: StackingPlanService,
    private filtersService: FiltersService,
    private spaceEventsService: SpaceEventsService,
    private spLinkOpportunityService: SpLinkOpportunityService,
    private localizationOptionsService: LocalizationOptionsService,
    private spStateViewService: StackPlanViewStateService,
    private tenantSummary: TenantSummaryService,
    private router: Router,
    // TODO: see if `IconLoaderService` this can be converted into APP_INITIALIZER
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    iconLoaderService: IconLoaderService
  ) {}

  ngOnInit() {
    this.#subscribeToStackingPlanLoads();
    this.updateStackingPlanData();
    this._listenViewStateChanges().pipe(untilDestroyed(this)).subscribe();

    merge(
      this.stackingPlanService.getSpaceChanges(),
      this.subscribeToSpaceReplacements(),
      this.subscribeToSpaceCreations()
      // allow refresh only on PV for now (in future stack detail could also perform this operations)
    )
      .pipe(
        untilDestroyed(this),
        // refresh SP data
        tap(this._refresh),
        filter(Boolean)
      )
      .subscribe(result => {
        const { entityName, remainingSpace, remainingSpaceUOM, floor, suiteNumber } = result;
        this.newRecordCreation({
          entityName,
          remainingSpace,
          remainingSpaceUOM,
          floor: remainingSpace ? floor : null,
          suiteNumber: remainingSpace ? suiteNumber : null,
        });
      });
    this.remainingSpaceFromInlineEdits();
    this.subscribeToLinkOpportunity();
    this.stackingPlanService.initSetCiqPath(this.localizationConfigs.ciqPath);
  }

  private subscribeToLinkOpportunity() {
    if (this.linkedOpportunityInput == null) return;
    this.spLinkOpportunityService.opportunityLinking$
      .pipe(
        untilDestroyed(this),
        switchMap(({ leaseToLink, record }) => this.linkedOpportunityInput({ leaseToLink, record })),
        filter(Boolean)
      )
      // Current functionality expects linkOpportunityResponse to be a DataObject. This will change once the actual linking between lease and the opportunity is made through an HTTP
      .subscribe(() => this._refresh());
  }

  ngOnDestroy() {
    this.stackingPlanService.resetSubjects();
    // Clean up data used for the Tenant Summary
    this.tenantSummary.clearTenantSummary();
    this.tenantSummary.clearFloorsSummary();
  }

  setPropertyType(type: string) {
    this.selectedPropertyType.set(type);
    this.spStateViewService.setPropertyType(type);
    this.selectedUsage.emit(type);
  }

  propertyViewChange(view: StackViewType) {
    if (view === StackViewType.PropertyView && this.buildingData == null) {
      this._getPropertyViewData(this.recordId);
    }
  }

  /** property/usage of the last load that warmed the filters cache, so smooth/NR/unaccounted toggles don't re-fetch filters nobody reads (QW-04) */
  #lastFilterLoadKey: string;

  /**
   * Fires stack2, issues, filters and settings in parallel instead of gating issues/filters/settings
   * behind stack2's response: none of them need stack2's data (QW-04). Every load, including `_refresh`
   * after a save, invalidates the previous load's caches first so a toolbar can never read stale data,
   * and warms fresh in-flight streams that the toolbars' own (unchanged) calls will reuse.
   *
   * The issues/filters prefetch itself only fires once this BE is known not to support the stack2
   * `include=filters,issues` bundle (HV-11F): `includeSupported` starts `undefined`, so the first
   * load of the session lets stack2's own response seed those caches (or, against an old BE, lets
   * the toolbars' calls fetch as they did before QW-04). Settings has no bundle equivalent and is
   * always prefetched.
   */
  private _loadStackingPlanData(propertyId: number, force = false) {
    this.stackingPlanService.invalidateLoadCaches();
    const state = this.spStateViewService.getState();
    this.stackingPlanService.getSettings().subscribe({ error: () => {} });

    if (this.stackingPlanService.includeSupported === false) {
      this.stackingPlanService.getPendingIssuesData(state, propertyId).subscribe({ error: () => {} });

      // filters toolbar only cares about propertyId/propertyType, not smooth/NR/unaccounted toggles
      const filterLoadKey = `${propertyId}|${state.propertyType}`;
      if (force || filterLoadKey !== this.#lastFilterLoadKey) {
        this.filtersService.prefetch(propertyId, this.localizationOptionsService.getInstance().localeId);
        this.#lastFilterLoadKey = filterLoadKey;
      }
    }

    this._getStackingPlanData(propertyId);

    if (this.buildingData != null) {
      this._getPropertyViewData(propertyId);
    }
  }

  #setReviewedBy = ({ reviewedBy, reviewedOn }) => {
    this.stackingPlanService.updateLastReviewedData({ reviewedBy, reviewedOn });
  };

  /**
   * From the Building Info build the data structure used for the Tenant Summary
   * @param resp
   */
  calculateDataByTenant = (resp: { results: Building }) => {
    const summaryMap = convertBuildingToTenantSummaryMap(resp.results);
    this.tenantSummary.setTenantSummary(summaryMap);
    this.tenantSummary.setFloorsSummary(resp?.results?.floors ?? []);
  };

  private _getPropertyViewData = (propertyId: number) => {
    const target = this.spStateViewService.getState()?.propertyType ?? 'All';
    const {
      isSmoothView: smooth = false,
      showNonRentableSpace: show_nr = false,
      showUnaccountedSpace: show_un = false,
    } = this.spStateViewService.getState();

    this.stackingPlanService
      .getPVStackPlan(propertyId, { target, smooth, show_nr, show_un })
      .pipe(
        tap((resp: { results: Building }) =>
          this.#setReviewedBy({
            reviewedBy: resp?.results?.building_info?.property_review_by,
            reviewedOn: resp?.results?.building_info?.property_review_ts,
          })
        ),
        tap(this.calculateDataByTenant)
      )
      .subscribe(res => (this.buildingData = res.results));
  };

  // TODO: move `getStackingPlanData` and it's child processes to to sp service
  private _getStackingPlanData(propertyId: number): void {
    // params are read at call time; the load itself runs in #subscribeToStackingPlanLoads
    this.#loadRequests.next({ id: propertyId, params: this.spStateViewService.getState() });
  }

  /** switchMap: a newer load cancels the in-flight one, so a stale stack2 response never overwrites a newer one */
  #subscribeToStackingPlanLoads() {
    this.#loadRequests
      .pipe(
        switchMap(({ id, params }) =>
          this.stackingPlanService.getStackingPlan(id, params).pipe(
            map(res => res.results),
            filter(Boolean),
            tap((sp: EDPStackingPlan) =>
              this.#setReviewedBy({ reviewedBy: sp?.property_review_by, reviewedOn: sp?.property_review_ts })
            ),
            // report like the old per-call path did, but keep the shared load stream alive
            catchError(error => {
              this.#errorHandler.handleError(error);
              return EMPTY;
            })
          )
        ),
        untilDestroyed(this)
      )
      .subscribe(edpStackingPlan => {
        const {
          elevator_bank,
          floors,
          net_rentable_area,
          net_rentable_area_uom_desc,
          total_gross_area,
          total_gross_area_uom_desc,
          total_number_of_floors,
          number_of_floor_below_ground,
          typical_floor_area,
          typical_floor_area_uom_desc,
          minimum_floor_area,
          minimum_floor_area_uom_desc,
          maximum_floor_area,
          maximum_floor_area_uom_desc,
          property_review_by,
          property_review_ts,
          total_vacancy_area,
          total_vacancy_area_uom_desc,
          dataStatus,
          is_selected_usage_primary,
          has_usage_primary,
        } = edpStackingPlan;

        const usages = edpStackingPlan.usages || [];
        const officeUsage = usages.find(usage => usage.ref_property_usage_type_desc === UsageTypes.Office);

        this.containerData = {
          elevatorBank: elevator_bank || [],
          floors: floors || [],
          netRentableArea: net_rentable_area,
          netRentableAreaUOM: net_rentable_area_uom_desc,
          totalGrossArea: total_gross_area,
          totalGrossAreaUOM: total_gross_area_uom_desc,
          totalGrossOfficeArea: officeUsage?.total_gross_area,
          totalGrossOfficeAreaUOM: officeUsage?.total_gross_area_uom_desc,
          totalNumberOfFloors: total_number_of_floors,
          totalNumberOfFloorsUnderground: number_of_floor_below_ground,
          commonFloorSize: typical_floor_area,
          commonFloorSizeUOM: typical_floor_area_uom_desc,
          minimumFloorArea: minimum_floor_area,
          minimumFloorAreaUOM: minimum_floor_area_uom_desc,
          maximumFloorArea: maximum_floor_area,
          maximumFloorAreaUOM: maximum_floor_area_uom_desc,
          totalVacancyArea: total_vacancy_area,
          totalVacancyAreaUOM: total_vacancy_area_uom_desc,
          dataStatus: dataStatus || SPDataStatus.Default,
          is_selected_usage_primary,
          has_usage_primary,
        };

        this.reviewerData = {
          reviewedBy: property_review_by,
          reviewedOn: property_review_ts,
        };

        this.stackingPlanService.setUsagesProperty(usages);
      });
  }

  private _listenViewStateChanges() {
    // reset/refresh on stack plan changes or view state changes
    return this.spStateViewService.onStateChange$.pipe(
      untilDestroyed(this),
      auditTime(100),
      tap(() => this._loadStackingPlanData(this.recordId))
    );
  }

  /**
   * Fetch the latest floor, elevator and toolbar data from more info dialog,
   * where the floor and elevator data updated through setUpdatedStackingData
   * custom service method. The latest fetched data will be updated in
   * {@link containerData} object, since this object is passing to child
   * components
   */
  updateStackingPlanData() {
    this.stackingPlanService
      .getUpdatedStackingData()
      .pipe(untilDestroyed(this), filter(Boolean))
      .subscribe(data => {
        /*
        If toolbar data(total number of floors or nra) has any changes?
        *it will emits the data to record details component and will update the data in edp
        */
        const toolbarData = data?.toolbarData;
        const containerData = this.containerData as StackingPlanContainer;
        if (
          toolbarData?.totalNumberOfFloors !== containerData.totalNumberOfFloors ||
          toolbarData?.netRentableArea !== containerData.netRentableArea ||
          toolbarData?.netRentableAreaUOM !== containerData.netRentableAreaUOM
        ) {
          this.updateEdpPropFloorData.emit(data.toolbarData);
        }

        this.assignContainerWithGivenData(data);
      });
  }

  private assignContainerWithGivenData(data: UpdateStackingPlanInfo) {
    if (data?.updatedFloorData || data?.elevatorBank) {
      const toolbar = data?.toolbarData;
      const containerData = this.containerData as StackingPlanContainer;
      this.containerData = {
        elevatorBank: data.elevatorBank ?? containerData.elevatorBank,
        floors:
          data.updatedFloorData?.filter(floor => floor.floor_index >= FloorClass.InvalidIndex) ?? containerData.floors,
        netRentableArea: toolbar?.netRentableArea ?? containerData.netRentableArea,
        netRentableAreaUOM: toolbar?.netRentableAreaUOM ?? containerData.netRentableAreaUOM,
        totalNumberOfFloors: toolbar?.totalNumberOfFloors ?? containerData.totalNumberOfFloors,
        totalNumberOfFloorsUnderground: toolbar?.underGroundFloors ?? containerData.totalNumberOfFloorsUnderground,
        commonFloorSize: toolbar?.commonFloorSize ?? containerData.commonFloorSize,
        commonFloorSizeUOM: toolbar?.commonFloorSizeUOM ?? containerData.commonFloorSizeUOM,
        minimumFloorArea: toolbar?.minimumFloorArea ?? containerData.minimumFloorArea,
        maximumFloorArea: toolbar?.maximumFloorArea ?? containerData.maximumFloorArea,
      } as StackingPlanContainer;
    }
  }

  private subscribeToSpaceCreations(): Observable<any> {
    return this.stackingPlanService.spaceCreation$.pipe(
      switchMap(spaceToCreate => {
        if (this.createSpace == null) {
          throw new Error('No createSpace function has been provided to the stacking plan component');
        }
        const createSpaceEvent = this.spaceEventsService.generateCreateSpaceEvent(this.recordId, spaceToCreate);
        const response$ = this.createSpace(createSpaceEvent);

        // send signal to reset filters. Step required due to new tenancy
        return response$.pipe(tap(() => this.filtersService.resetFilter$.next()));
      })
    );
  }

  private subscribeToSpaceReplacements(): Observable<any> {
    return this.stackingPlanService.spaceReplacement$.pipe(
      switchMap(spaceToReplace => {
        if (this.replaceSpace == null) {
          throw new Error('No replaceSpace function has been provided to the stacking plan component');
        }

        const replaceSpaceEvent = this.spaceEventsService.generateReplaceSpaceEvent(this.recordId, spaceToReplace);
        const response$ = this.replaceSpace(replaceSpaceEvent);

        // send signal to reset filters. Step required due to new tenancy
        return response$.pipe(tap(() => this.filtersService.resetFilter$.next()));
      })
    );
  }

  /**
   * Refresh the stacking plan data when:
   * - A new space is created
   * - A space is updated
   * - A space is replaced
   */
  private _refresh = () => {
    this._loadStackingPlanData(this.recordId, true);
    this.filtersService.resetFilter$.next();
  };

  public newRecordCreation(space: EfficiencyTransport) {
    const selectedUsage = this.spStateViewService.getState().propertyType;
    this.stackingPlanService.createSpace({
      entityName: space.entityName,
      propertyId: this.recordId,
      usage: selectedUsage,
      containerRecordFloors: this.containerData.floors || [],
      remainingSpace: space.remainingSpace,
      remainingSpaceUOM: space.remainingSpaceUOM,
      floor: space.floor,
      suiteNumber: space.suiteNumber,
      ownerOccupied: space.ownerOccupied,
    });
  }

  newRecordCreationRoute(space: EfficiencyTransport) {
    const selectedUsage = this.spStateViewService.getState()?.propertyType ?? '';
    const route =
      space?.entityName === 'availability' ? ['/records', 'availability', 'new'] : ['/records', 'lease', 'new'];
    const queryParams = {
      'property-id': this.recordId,
      'property-type': selectedUsage,
      closeTab: true,
    };
    const urlTree = this.router.createUrlTree(route, { queryParams });
    const url = globalThis.location.origin + this.router.serializeUrl(urlTree);
    globalThis.open(url, '_blank');
  }

  private remainingSpaceFromInlineEdits() {
    this.stackingPlanService
      .getNewAvailabityTenancyCreation()
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        if (response) {
          const { remainingSpace, remainingSpaceUOM, floor, suiteNumber } = response;
          this.newRecordCreation({
            entityName: null,
            remainingSpace,
            remainingSpaceUOM,
            floor: remainingSpace ? floor : null,
            suiteNumber: remainingSpace ? suiteNumber : null,
          });
        }
      });
  }
}
